import { useQuery } from "@tanstack/react-query";
import { bbox } from "@turf/bbox";
import bboxPolygon from "@turf/bbox-polygon";
import { buffer } from "@turf/buffer";
import { point } from "@turf/helpers";
import { useDebounce } from "@uidotdev/usehooks";
import { useAtom } from "jotai";
import { DateTime } from "luxon";
import { useEffect, useMemo, useRef, useState } from "react";
import type {
    AerialImagesList,
    EmissionRecordsListRequest,
    Equipment,
} from "../../../../apiClient/generated";
import {
    useEmissionRecordsApiClient,
    useInfrastructureApiClient,
    useMapApiClient,
} from "../../../../hooks";
import { createFeatureCollection } from "../../../../utils/geopatialUtils";
import type { EmissionRecordMapWithBin } from "../../../MapV2/CustomTypes";
import { EMISSION_LAYER_STYLES } from "../../../MapV2/constants";
import { PlumeControl } from "../../../MapV2/elements/DataControlsV2/PlumeControl";
import { useEmissionRecordsOnMap } from "../../../MapV2/hooks/emissions";
import { useMapData } from "../../../MapV2/hooks/mapDataAndFilters";
import { useMap } from "../../../MapV2/hooks/mapState";
import {
    EquipmentLayer,
    SiteLayer,
} from "../../../MapV2/layers/infrastructure";
import { RasterLayer } from "../../../MapV2/layers/rasters";
import { MapBase } from "../../../MapV2/Map";
import { MapPopup, type PickedItems } from "../../../MapV2/MapPopup";
import { endOfUtcDay, hoverContextAtom, selectionContextAtom } from "../state";

interface SiteMapProps {
    siteId: string;
    dateFrom: Date;
    dateTo: Date;
    filters?: EmissionRecordsListRequest;
}

export const SiteMap = (props: SiteMapProps) => {
    const infrastructureApiClient = useInfrastructureApiClient();
    const mapApiClient = useMapApiClient();
    const { flyTo } = useMap(`site-${props.siteId}`);
    const [pickedItems, setPickedItems] = useState<PickedItems>();
    const containerRef = useRef<HTMLDivElement>();

    // Selection context
    const [selectionContext, setSelectionContext] =
        useAtom(selectionContextAtom);

    // Hover context
    const [hoverContext, setHoverContext] = useAtom(hoverContextAtom);

    // Selected emission data, if any
    const apiClient = useEmissionRecordsApiClient();
    const emissionQuery = useQuery({
        queryKey: ["emissionRecord", selectionContext.emissionId],
        queryFn: async () => {
            const response = await apiClient.emissionRecordsRetrieve({
                id: selectionContext.emissionId,
            });
            return response;
        },
        refetchOnWindowFocus: false,
        enabled: !!selectionContext.emissionId,
        staleTime: Infinity,
    });

    // Map settings
    const mapName = useMemo(() => `site-${props.siteId}`, [props.siteId]);
    const { selectedContext, setSelectedContext } = useMapData(mapName);

    // Narrow the emission points to a single record.
    // Selection is the first priority, then the hover behavior.
    const filterEmissionPoints =
        !!selectedContext.emissionRecordId || hoverContext?.source !== "map";

    // Sync date range from timeline
    const debouncedDates = useDebounce(selectionContext.dateRange, 50);
    useEffect(() => {
        if (!debouncedDates) {
            setSelectedContext((s) =>
                s.dateRangeToShowData === undefined
                    ? s
                    : { ...s, dateRangeToShowData: undefined },
            );
            return;
        }
        setSelectedContext((s) => ({
            ...s,
            dateRangeToShowData: {
                start: debouncedDates.start.toISOString(),
                end: debouncedDates.end.toISOString(),
            },
        }));
    }, [debouncedDates, setSelectedContext]);

    // Sync selected emission and plume - the paged linked record when there is
    // one, the main record otherwise. `focusedPlumeId` is null for a record
    // with no plume, which undefined (no focus at all) must not collapse into.
    useEffect(() => {
        if (selectionContext.emissionId && emissionQuery.data) {
            const plumeId =
                selectionContext.focusedPlumeId !== undefined
                    ? selectionContext.focusedPlumeId
                    : emissionQuery.data.dataPoint.plumeImage;
            setSelectedContext((s) => ({
                ...s,
                emissionRecordId:
                    selectionContext.focusedEmissionId ?? emissionQuery.data.id,
                relatedPlume: plumeId,
                // Pin to the focused plume: left undefined this falls through
                // to the +/-7 day window around the originally clicked record,
                // blanking the plume of a linked record detected further out.
                plumeIds: plumeId ? [plumeId] : undefined,
            }));
        }
        if (!selectionContext.emissionId && selectedContext.emissionRecordId) {
            setSelectedContext((s) => ({
                ...s,
                emissionRecordId: undefined,
                relatedPlume: undefined,
                plumeIds: undefined,
                dateRangeToShowData: undefined,
            }));
        }
    }, [
        selectionContext.emissionId,
        selectionContext.focusedEmissionId,
        selectionContext.focusedPlumeId,
        setSelectedContext,
        selectedContext.emissionRecordId,
        emissionQuery.data,
    ]);

    // Sync hover context (row/timeline hover) into map filters. A selection
    // owns the map, so a hover must not narrow it while one is active.
    useEffect(() => {
        const hasSelection = !!selectionContext.emissionId;
        setSelectedContext((s) => ({
            ...s,
            emissionRecordIds: hasSelection
                ? undefined
                : hoverContext?.emissionIds,
            // The effect above owns plumeIds while a selection is open.
            plumeIds: hasSelection
                ? s.plumeIds
                : hoverContext?.relatedPlumeImages,
        }));
    }, [hoverContext, selectionContext.emissionId, setSelectedContext]);

    // Site data
    const siteQuery = useQuery({
        queryKey: ["site", props.siteId],
        queryFn: async () => {
            const response =
                await infrastructureApiClient.infrastructureSitesRetrieve({
                    id: props.siteId,
                });
            return response;
        },
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    });
    useEffect(() => {
        if (siteQuery.data?.location) {
            const timeout = setTimeout(() => {
                flyTo(
                    siteQuery.data.location.coordinates[1],
                    siteQuery.data.location.coordinates[0],
                    14,
                    true,
                );
            }, 400);
            return () => clearTimeout(timeout);
        }
    }, [siteQuery.data, flyTo]);
    // Aerial images
    const aerialImageQuery = useQuery({
        queryKey: [
            "siteAerialImages",
            props.siteId,
            props.dateFrom.toISOString(),
            props.dateTo.toISOString(),
        ],
        queryFn: async () => {
            const response = await mapApiClient.mapAerialImagesList({
                site: [props.siteId],
                takenAtDataRangeAfter: props.dateFrom,
                takenAtDataRangeBefore: props.dateTo,
            });
            return response;
        },
        refetchOnWindowFocus: false,
        staleTime: Infinity,
    });
    // Compute aerial image layers to display - a single image for the site:
    // the newest one taken on or before the end of the timeline selection
    // (how the site looked at that time). Aerial images are sparse, so a
    // narrow selection (e.g. +/-7 days around a selected emission) rarely
    // contains one - only the end of the selection is used. If every image
    // is newer than the selection, fall back to the oldest one.
    // The query covers the whole page range, so this never triggers a refetch.
    // `takenAt` is day-precision (midnight UTC), so the selection end is
    // widened to the whole UTC day.
    const aerialImageLayers = useMemo(() => {
        const rangeEnd = endOfUtcDay(debouncedDates?.end ?? props.dateTo);
        const images = aerialImageQuery.data?.results || [];
        const newestBeforeEnd = images
            .filter(
                (aerialImage: AerialImagesList) =>
                    aerialImage.takenAt <= rangeEnd,
            )
            .reduce<AerialImagesList | undefined>(
                (newest, current) =>
                    !newest || current.takenAt > newest.takenAt
                        ? current
                        : newest,
                undefined,
            );
        const imageToShow =
            newestBeforeEnd ??
            images.reduce<AerialImagesList | undefined>(
                (oldest, current) =>
                    !oldest || current.takenAt < oldest.takenAt
                        ? current
                        : oldest,
                undefined,
            );

        return [
            RasterLayer({
                data: imageToShow ? [imageToShow] : [],
                name: "aerial_image",
                visible: true,
                zoomLevel: 20,
                opacity: 1,
            }),
        ];
    }, [aerialImageQuery.data, debouncedDates, props.dateTo]);

    // Equipment data
    const equipmentQuery = useQuery({
        queryKey: ["equipmentForSiteResults", props.siteId],
        queryFn: async () => {
            const response =
                await infrastructureApiClient.infrastructureEquipmentList({
                    site: [props.siteId],
                    pageSize: 200,
                });
            return response.results;
        },
        refetchOnWindowFocus: false,
    });

    // Nearby sites
    const sitesNearbyQuery = useQuery({
        queryKey: ["siteNearLocation", siteQuery.data?.location],
        queryFn: async () => {
            if (!siteQuery.data) {
                return [];
            }
            // Compute a 1km radius around point
            const bufferedArea = buffer(
                point(siteQuery.data.location.coordinates as [number, number]),
                1,
                { units: "kilometers" },
            );
            if (!bufferedArea) {
                return [];
            }
            // Retrieve sites
            const response =
                await infrastructureApiClient.infrastructureSitesList({
                    locationWithin: JSON.stringify(
                        bboxPolygon(bbox(bufferedArea)).geometry,
                    ),
                    pageSize: 30,
                });
            return response.results;
        },
        enabled: !!siteQuery.data,
        refetchOnWindowFocus: false,
    });

    // Emission data
    const thirdPartyEmissions = useEmissionRecordsOnMap(
        mapName,
        true,
        `${mapName}-third-party`,
        false,
        useMemo(
            () => ({
                ...props.filters,
                site: undefined,
                detectionDateRangeAfter: props.dateFrom,
                detectionDateRangeBefore: props.dateTo,
                dataSource: ["THIRD_PARTY"],
                relatedSite: props.siteId,
                providerWithSource: props.filters.providerWithSource ?? "all",
            }),
            [props.dateFrom, props.dateTo, props.siteId, props.filters],
        ),
        EMISSION_LAYER_STYLES.thirdParty,
        undefined,
        filterEmissionPoints,
    );

    const selfReportedEmissions = useEmissionRecordsOnMap(
        mapName,
        true,
        `${mapName}-self-reported`,
        false,
        useMemo(
            () => ({
                ...props.filters,
                site: undefined,
                detectionDateRangeAfter: props.dateFrom,
                detectionDateRangeBefore: props.dateTo,
                dataSource: ["SELF_REPORTED"],
                relatedSite: props.siteId,
                providerWithSource: props.filters.providerWithSource ?? "all",
            }),
            [props.dateFrom, props.dateTo, props.siteId, props.filters],
        ),
        EMISSION_LAYER_STYLES.operatorProvided,
        undefined,
        filterEmissionPoints,
    );

    // Map layers
    const layers = [
        ...aerialImageLayers,
        ...selfReportedEmissions.plumeLayers,
        ...thirdPartyEmissions.plumeLayers,
        SiteLayer(
            createFeatureCollection(
                sitesNearbyQuery.data ? sitesNearbyQuery.data : [],
            ).features,
            true,
            16,
            undefined,
            undefined,
            0.1,
            "nearbySites",
        ),
        SiteLayer(
            createFeatureCollection(siteQuery.data ? [siteQuery.data] : [])
                .features,
            true,
            16,
        ),
        EquipmentLayer(
            createFeatureCollection(
                equipmentQuery.data ? equipmentQuery.data : [],
            ).features,
            true,
            16,
        ),
        ...selfReportedEmissions.emissionLayers,
        ...thirdPartyEmissions.emissionLayers,
    ];

    // While a selection is open a map click only moves the focus - repointing
    // `emissionId` would remount the detail panel and close the open form.
    const selectEmissionFromMap = (properties: EmissionRecordMapWithBin) => {
        const emissionTimestamp = DateTime.fromJSDate(
            properties.detectionTimestamp,
        );
        const highlightPosition = {
            date: emissionTimestamp.toJSDate(),
            x: 0,
        };
        setSelectionContext((s) =>
            s.emissionId
                ? {
                      ...s,
                      focusedEmissionId: properties.id,
                      focusedPlumeId: properties.plumeImage,
                      highlightPosition,
                  }
                : {
                      ...s,
                      emissionId: properties.id,
                      dateRange: {
                          start: emissionTimestamp
                              .minus({ days: 7 })
                              .toJSDate(),
                          end: emissionTimestamp.plus({ days: 7 }).toJSDate(),
                      },
                      highlightPosition,
                      focusedEmissionId: undefined,
                      focusedPlumeId: undefined,
                  },
        );
    };

    return (
        <div
            ref={containerRef}
            className="relative w-full rounded overflow-hidden h-full"
        >
            <MapBase
                layers={layers}
                mapId={`site-${props.siteId}`}
                showBasemapControl
                showMeasureControl
                showScaleControl
                showZoomControl
                onLeftClick={({ info, event }) => {
                    const emissions = info.filter(
                        (i) => i.object && i.layer.id.includes("emission-"),
                    );
                    if (emissions.length === 0) {
                        setPickedItems(undefined);
                    } else if (emissions.length === 1) {
                        selectEmissionFromMap(
                            emissions[0].object
                                .properties as EmissionRecordMapWithBin,
                        );
                    } else {
                        const containerRect =
                            containerRef.current.getBoundingClientRect();
                        setPickedItems({
                            pointClicked: {
                                x: event.clientX - containerRect.left,
                                y: event.clientY - containerRect.top,
                            },
                            sites: [],
                            siteClusters: [],
                            equipment: [],
                            pipelines: [],
                            emissions: emissions,
                            emissionClusters: [],
                            dataPoints: [],
                            unmatchedDataPoints: [],
                            extraData: [],
                        });
                    }
                }}
                onHover={(info) => {
                    if (!info.object || !info.layer?.id.includes("emission-")) {
                        // Only clear if there's actually something to clear.
                        if (hoverContext !== undefined) {
                            setHoverContext(undefined);
                        }
                        return;
                    }

                    // Set hover context (only if it changed) - map can only highlight one at a time
                    const emissionData = info.object
                        .properties as EmissionRecordMapWithBin;
                    if (hoverContext?.emissionIds?.[0] !== emissionData.id) {
                        setHoverContext({
                            emissionIds: [emissionData.id],
                            relatedPlumeImages: [emissionData.plumeImage],
                            source: "map",
                        });
                    }
                }}
                getTooltip={(info) => {
                    if (!info.object?.properties) {
                        return;
                    }
                    if ("equipmentType" in info.object.properties) {
                        const equipment: Equipment = info.object.properties;
                        return `${equipment.equipmentClass || equipment.groupName} ${equipment.operatorUniqueId ? "(" + equipment.operatorUniqueId + ")" : ""}`;
                    }
                    if ("detectedRate" in info.object.properties) {
                        return `${info.object.properties.detectedRate / 1000} kg/h`;
                    }
                    if ("siteName" in info.object.properties) {
                        return info.object.properties.siteName;
                    }
                }}
            />

            {pickedItems && (
                <MapPopup
                    picked={pickedItems}
                    onClickItem={(item) => {
                        setPickedItems(undefined);
                        selectEmissionFromMap(
                            item.object.properties as EmissionRecordMapWithBin,
                        );
                    }}
                />
            )}

            <div className="absolute top-2 w-full flex justify-center">
                <PlumeControl mapId={mapName} variant="sm" />
            </div>
        </div>
    );
};
